import { useId, useRef } from "react";
import type { ReactNode, RefObject } from "react";
import { useDialogTrap } from "@/hooks/useDialogTrap";
import "./DialogShell.css";

export type DialogSize = "sm" | "md" | "lg";

export interface DialogShellProps {
  /** The visible heading AND the dialog's accessible name (`aria-labelledby` points at it). Rendered as an `h2`. */
  title: ReactNode;
  /** Only when the caller needs the heading's id (an `aria-describedby`/`aria-labelledby` of its own); generated otherwise. */
  titleId?: string;
  /** `alertdialog` for a confirmation that interrupts (delete, reset, archive); `dialog` for everything else. */
  role?: "dialog" | "alertdialog";
  /** `md` (420px, the look of Add livestock) unless the content needs less (`sm`, a confirm) or more (`lg`, a table of rows). */
  size?: DialogSize;
  /** Escape, an overlay click, and whatever the caller wires its own Cancel to. */
  onClose: () => void;
  /** A save/delete is in flight: the panel reports `data-state="saving"` + `aria-busy`, and (unless `dismissWhileBusy`) Escape
   *  and an overlay click do nothing so the dialog stays up to show the outcome. */
  busy?: boolean;
  /** For a dialog whose controls save as you go (Customize Home): `busy` is reported but Escape / the scrim still close it. */
  dismissWhileBusy?: boolean;
  /** The actions row, right-aligned: Cancel then the primary action LAST. A form's submit button names its form with `form={id}`. */
  footer?: ReactNode;
  /** `aria-describedby` for a confirm's explanatory paragraph. */
  describedBy?: string;
  /** Overrides `data-state` on the panel (default: `saving` | `ready`). */
  dataState?: string;
  /** Focus this on open instead of the first field (a confirm focuses its safe button). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Give focus back to the element that had it when the dialog opened. Callers that move focus themselves leave this off. */
  restoreFocus?: boolean;
  /** The scrollable body: the dialog's form / content. */
  children: ReactNode;
}

/**
 * ONE modal surface for the whole app — overlay, panel, heading, body, footer — so every dialog looks and behaves the same
 * on a phone and a desktop. Sizing, radius, padding and scrim live in DialogShell.css; a dialog supplies only its content.
 *
 * Behaviour: focus moves in on open and is trapped; Escape and a click on the scrim close (unless `busy`); the panel scrolls
 * as a whole and is capped to the visible viewport by `styles/dialog-viewport.css` (which keys on `aria-modal`, so it also
 * covers the iOS keyboard). Mount it only while open — it has no `open` prop on purpose.
 *
 * Breadcrumbs (ui-breadcrumbs.md): `data-dialog-shell` + `data-dialog-size` on the panel, `data-dialog-footer` on the actions
 * row, `data-state` = `ready` | `saving`. `e2e/dialog-shell-consistency.spec.ts` reads them.
 */
export function DialogShell({
  title,
  titleId,
  role = "dialog",
  size = "md",
  onClose,
  busy = false,
  dismissWhileBusy = false,
  footer,
  describedBy,
  dataState,
  initialFocus,
  restoreFocus,
  children,
}: DialogShellProps) {
  const generatedId = useId();
  const headingId = titleId ?? generatedId;
  const panelRef = useRef<HTMLDivElement>(null);
  const locked = busy && !dismissWhileBusy;
  useDialogTrap(panelRef, onClose, { busy: locked, initialFocus, restoreFocus });

  return (
    <div className="dialog-shell" onClick={locked ? undefined : onClose}>
      <div
        ref={panelRef}
        className="dialog-shell__panel"
        role={role}
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={describedBy}
        aria-busy={busy ? true : undefined}
        data-dialog-shell=""
        data-dialog-size={size}
        data-state={dataState ?? (busy ? "saving" : "ready")}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={headingId} className="dialog-shell__title">
          {title}
        </h2>
        <div className="dialog-shell__body">{children}</div>
        {footer ? (
          <div className="dialog-shell__footer" data-dialog-footer="">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
